// Call card & dispatch helpers — extracted from legacy-utils.ts

import { hasValue, hasStringValue } from './validation';
import { isInArray, isInArrayByProperty, generatePropertyToTrueObject } from './arrays';
import { getCurrentCompanyId, getAuthObject, getModuleType, getAvailableModules, getAvailableSubmodules, currentUserHasPermission, getOfficerLabel } from './auth';
import { timeSince } from './date-time';
import { getGeolocation } from './geolocation';
import { App } from '@/services/app-compat';
import { ModuleType } from '@/constants/codes';

const _w = window as any;

export function displayNocValuesForGridTemplate(e: any): string | undefined {
  if (!hasValue(e.natureOfCallValues)) {
    return;
  }
  return e.natureOfCallValues.join(", ");
}

export function closeNotificationModals(): void {
  if (typeof _w._panicDialogState !== 'undefined' && _w._panicDialogState) {
    _w._panicDialogState.isOpen = false;
    _w._renderPanicDialog();
  }
  if (typeof _w._officerAssignedState !== 'undefined' && _w._officerAssignedState) {
    _w._officerAssignedState.isOpen = false;
    _w._renderOfficerAssignedDialog();
  }
  if (typeof _w._caseReportSubmittedIsOpen !== 'undefined' && _w._caseReportSubmittedIsOpen) {
    _w._caseReportSubmittedIsOpen = false;
    _w._closeCaseReportSubmittedReact();
  }
}

export function getGridBackupUnitClassNameFromOfficerStatus(officerStatus: any): string {
  if (officerStatus === "c") {
    return "label officer-status-label-OrnaCleared";
  } else if (officerStatus === "o") {
    return "label officer-status-label-OrnaOnScene";
  } else if (officerStatus === "e") {
    return "label officer-status-label-OrnaEnroute";
  } else if (officerStatus === "d") {
    return "label officer-status-label-OrnaDispatched blink_me";
  } else {
    return "label officer-status-label-danger";
  }
}

export function getCADInProgressPendingClassNameFromCallDateTime(callDateTime: any): string {
  const timeSinceCallDateTime: number = timeSince(callDateTime) as any;
  const timeDiffMinutes: number = (timeSinceCallDateTime / 1000.0) / 60.0;
  if (timeDiffMinutes < 5) {
    return "call-label call-label-StatusGreen";
  } else if (timeDiffMinutes < 15) {
    return "call-label call-label-StatusYellow";
  } else if (timeDiffMinutes < 30) {
    return "call-label call-label-StatusOrange";
  } else if (timeDiffMinutes < 45) {
    return "call-label call-label-StatusRed";
  } else if (timeDiffMinutes >= 45) {
    return "call-label call-label-StatusRed blink_me";
  }
  return "";
}

export function processCallCardAvailableOfficers(result: any): void {
  for (let officer of result.officerList) {
    var company = result.companyList.find((n: any) => n.id === officer.companyId);
    officer.company = company.name;
  }

  if (result && result.officerList) {
    result.officerList.forEach((officer: any) => {
      if (!officer.hasOwnProperty('manualStatus')) {
        officer.manualStatus = null;
      }
      if (!officer.hasOwnProperty('callCard')) {
        officer.callCard = null;
      }
      if (!officer.hasOwnProperty('eventId')) {
        officer.eventId = null;
      }
    });
  }
}

export function getOfficerListStatusImageTemplateFromOfficer(officer: any): string {
  if (officer.manualStatus == null) {
    return '<img src="/Content/Images/OfficerList/officerOffline.svg" style="width:10px;height:10px;vertical-align: middle;" />';
  } else if (officer.callCard !== null && officer.callCard !== undefined) {
    return '<img src="/Content/Images/OfficerList/officerOnCall.svg" style="width:10px;height:10px;vertical-align: middle;" />';
  } else if (officer.manualStatus === 1) {
    return '<img src="/Content/Images/OfficerList/officerOnline.svg" style="width:10px;height:10px;vertical-align: middle;" />';
  } else if (officer.manualStatus === 2) {
    return '';
  } else {
    return '';
  }
}

function sortByCompanyAndIndex<T extends { companyId: number, listIdx: number }>(
  data: T[],
  getTiebreaker: (item: T) => number,
  getLabel: (item: T) => string
): void {
  const currentCompanyId: number = getCurrentCompanyId();

  // Current company's items first, then each officer company's block, then the
  // "Multiple Other Companies" bucket (companyId -1) last.
  const companyRank = function (companyId: number): number {
    if (companyId === currentCompanyId) return 0;
    if (companyId === -1) return 2;
    return 1;
  };

  data.sort(function (a, b) {
    const rankA = companyRank(a.companyId);
    const rankB = companyRank(b.companyId);
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    if (a.companyId !== b.companyId) {
      return a.companyId < b.companyId ? -1 : 1;
    }
    if (rankA === 2) {
      // The multiple-company bucket has no meaningful listIdx; sort it alphabetically.
      const labelCompare = getLabel(a).localeCompare(getLabel(b), undefined, { sensitivity: 'base' });
      if (labelCompare !== 0) {
        return labelCompare;
      }
    }
    if (a.listIdx !== b.listIdx) {
      return a.listIdx < b.listIdx ? -1 : 1;
    }
    const aVal = getTiebreaker(a);
    const bVal = getTiebreaker(b);
    return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
  });
}

export function sortNatureOfCallList(data: Array<{ companyId: number, listIdx: number, code: number, value?: string }>): void {
  sortByCompanyAndIndex(data, item => item.code, item => item.value || '');
}

export function sortCallDispositionList(data: Array<{ companyId: number, listIdx: number, dispositionCode: number, dispositionValue?: string }>): void {
  sortByCompanyAndIndex(data, item => item.dispositionCode, item => item.dispositionValue || '');
}

// True when a cross-company dispatcher's merged list contains items owned by a
// company other than the user's own (officer companies or the -1 "multiple" bucket).
export function listHasOtherCompanyItems(list: Array<{ companyId?: number | null }>): boolean {
  if (!hasValue(list)) {
    return false;
  }
  const currentCompanyId = getCurrentCompanyId();
  return list.some(item => hasValue(item.companyId) && item.companyId !== currentCompanyId);
}

export function showCompanyNotification(pubnubMessage: any): void {
  App.showPopupNotification(pubnubMessage.notification, "success");
}

export function addCompanyNameField(natureOfCallList: any, companyList: any): void {
  if (!hasValue(natureOfCallList) || !hasValue(companyList)) {
    return;
  }
  const companyIdToName = companyList.reduce(function (result: any, item: any) {
    result[item.id.toString()] = item.name;
    return result;
  }, {});

  const currentCompanyId = getCurrentCompanyId();
  for (let natureOfCall of natureOfCallList) {
    if (!hasValue(natureOfCall.companyId)) {
      natureOfCall.companyName = 'Other';
    } else if (natureOfCall.companyId == -1) {
      natureOfCall.companyName = "Multiple Other Companies";
    } else if (natureOfCall.companyId == currentCompanyId) {
      // The user's own items render without a group header (an empty group name is
      // not rendered). Most of their codes are shared with officer companies, so a
      // header with the company's name would wrongly imply exclusive ownership.
      // Headers only introduce OTHER companies' items.
      natureOfCall.companyName = '';
    } else {
      natureOfCall.companyName = companyIdToName[natureOfCall.companyId.toString()];
    }
  }
}

export function mergeCurrentNatureOfCallIntoNatureOfCallList(currentNatureOfCallCodes: Array<number>, currentNatureOfCallValues: Array<string>,
  natureOfCallList: Array<{ code: number, value: string, companyId: number, listIdx: number, hasSop: boolean, priority: number | null }>):
  Array<{ code: number, value: string, companyId: number, listIdx: number, hasSop: boolean, priority: number | null }> {
  if (!hasValue(natureOfCallList)) {
    return [];
  }
  if (!hasValue(currentNatureOfCallCodes)) {
    return natureOfCallList;
  }

  const availableNocCodeToTrueMap = generatePropertyToTrueObject(natureOfCallList, 'code');

  const companyId = getCurrentCompanyId();
  const nocListItem: Array<{ code: number, value: string, companyId: number, listIdx: number, hasSop: boolean, priority: number | null }> = [];
  for (let i = 0; i < currentNatureOfCallCodes.length; i++) {
    const code = currentNatureOfCallCodes[i];
    if (!hasValue(availableNocCodeToTrueMap[code])) {
      const value = currentNatureOfCallValues[i];
      nocListItem.push({ code: code, value: value, companyId: companyId, listIdx: -1, hasSop: false, priority: null });
    }
  }

  return nocListItem.concat(natureOfCallList);
}

export function mergeCurrentCallDispositionIntoCallDispositionList(currentCallDisposition: number, currentCallDispositionValue: string,
  callDispositionList: Array<{ dispositionCode: number, dispositionValue: string, companyId: number, listIdx: number }>):
  Array<{ dispositionCode: number, dispositionValue: string, companyId: number, listIdx: number }> {
  if (!hasValue(callDispositionList)) {
    return [];
  }

  if (hasValue(currentCallDisposition) && !isInArrayByProperty(callDispositionList, 'dispositionCode', currentCallDisposition)) {
    const companyId = getCurrentCompanyId();
    return [{
      dispositionCode: currentCallDisposition,
      dispositionValue: currentCallDispositionValue,
      companyId: companyId,
      listIdx: -1
    }].concat(callDispositionList);
  }

  return callDispositionList;
}

export function goToNewCallWithDispositionNotes(dispositionNotes: string): void {
  const selectModuleCallback = (window as any).selectModuleCallback;
  const setupCallbackForDispositionNotesOnCurrentCallLoaded = (window as any).setupCallbackForDispositionNotesOnCurrentCallLoaded;

  if (!getAuthObject()) {
    App.showPopupNotification("User must log into Orna to use this integration.", "error");
    return;
  }
  const navigateToNewCall = () => window.OrnaReact.navigate.to({ section: 'CAD', tab: 'CallCard', fn: 'CAD_CallCard', multiModule: true });
  if (App.getModuleTypeFromURL() === ModuleType.cad) {
    setupCallbackForDispositionNotesOnCurrentCallLoaded(dispositionNotes);
    navigateToNewCall();
  } else {
    if (isInArray(getAvailableModules(), 'cad')) {
      (window as any).api.selectModule("cad", (result: any) => {
        selectModuleCallback(result);
        setupCallbackForDispositionNotesOnCurrentCallLoaded(dispositionNotes);
        navigateToNewCall();
      }, () => { });
    } else {
      App.showPopupNotification("Dispatch access required", "error");
    }
  }
}

export function dialogCheckIfUserWantsToEditOrReopenCallCardWithClientNotifications(clientId: number, nextStep: Function): void {
  const kstConfirm = (window as any).kstConfirm;

  if (hasValue(clientId)) {
    (window as any).api.getClientNotificationSummary(clientId, (result: any) => {
      if (result.callCard) {
        kstConfirm({
          message: "The client on this call is set up to be notified if this call card is (re)closed. Note that call comments are updated separately and can be added/modified without reclosing the call.",
          confirmText: 'Continue', cancelText: 'Cancel', confirmClass: 'success'
        }).then(function (value: any) {
          if (value) {
            nextStep();
          }
        });
      } else {
        nextStep();
      }
    }, (failResult: any) => {
      App.showPopupNotification("Error detecting if client notification would be sent.", "error");
    });
  } else {
    nextStep();
  }
}

export function getCompanyVehicleListForOfficerStatusPromise(authTokenRefresh: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    let submodules: Array<string> = getAvailableSubmodules();
    if (submodules === null) {
      submodules = [];
    }
    if (!isInArray(submodules, _w.AccessAuthorities.ROLE_ASSIGN_OFFICER_TO_COMPANY_VEHICLE)) {
      resolve({ companyVehicles: [] });
      return;
    }

    (window as any).api.listCompanyVehicles(false, true, authTokenRefresh, (result: any) => {
      resolve(result);
    }, (failResult: any) => {
      App.showPopupNotification("Error getting assigned vehicles.", "error");
      reject(failResult);
    });
  });
}

export function getOfficerStatusPromise(authTokenRefresh: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    (window as any).api.getOfficerStatus(authTokenRefresh, (result: any) => {
      resolve(result);
    }, (failResult: any) => {
      App.showPopupNotification(`Error getting ${getOfficerLabel().toLowerCase()} status.`, "error");
      reject(failResult);
    });
  });
}

export function updateStatusWithGeolocation(statusComment: string): void {
  getGeolocation((position: GeolocationPosition) => {
    (window as any).api.statusUpdate({ statusComment: statusComment, latitude: position.coords.latitude, longitude: position.coords.longitude }, (result: any) => {
      window.dispatchEvent(new CustomEvent('orna:status-updated'));
    }, (errorResult: any) => {
      App.showPopupNotification("Unable to update status", "error");
    });
  }, (error: GeolocationPositionError) => {
    (window as any).api.statusUpdate({ statusComment: statusComment, latitude: null, longitude: null }, (result: any) => {
      window.dispatchEvent(new CustomEvent('orna:status-updated'));
    }, (errorResult: any) => {
      App.showPopupNotification("Unable to update status", "error");
    });
  });
}
